/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * ============================================================================
 * METRIC RESOLVER — LIVE (backlog item 231)
 * ============================================================================
 * The module's promise is that progress updates on its own because depletions
 * already flow through Brand Command. This is where that happens. Given a goal's
 * metric, the program's window, its distributor, and the goal's target accounts,
 * it returns the achieved actual — pulled from the tenant's own depletion
 * reports through the documented cross-module RPC contract (depletionsContract),
 * never a table read and never a cross-module import.
 *
 * WHAT RESOLVES AUTOMATICALLY
 *   cases / depletions  sum of case_sales across the program window, restricted
 *                       to the program's distributor and (when set) the goal's
 *                       target accounts.
 *   new_pods            doors that sold during the window and had NOT sold in
 *                       the 12 months before it. This is a real door count, not
 *                       a states-entered proxy — migration 0070 gave depletions
 *                       an outlet dimension, which is what made it answerable.
 *
 * WHAT DOES NOT, AND WHY (these are properties of the GOAL, not missing plumbing)
 *   custom / other      free-form by definition — there is nothing to sum.
 *   SKU-scoped goals    depletion reports roll up per door, not per SKU, so a
 *                       goal narrowed to specific SKUs is scored from the
 *                       distributor's SKU-level report instead.
 *   no window           a goal with no start/end date has no period to sum over.
 *   no depletion rows   nothing has been reported for that distributor + window
 *                       yet. Returning 0 would read as "they sold nothing";
 *                       "nothing reported yet" is the true statement.
 *
 * THE CAPTURE MODEL IS UNCHANGED. A resolved value PRE-FILLS the scoring field;
 * the rep confirms it and `saveScore` writes the number into incentive_scores as
 * an immutable capture (tagged resolved_source='system' if they accepted it
 * unchanged, 'manual_upload' if they overrode it). Progress shown before scoring
 * is live and explicitly labelled as such.
 */

import { outletRollup, baselineWindow } from "./depletionsContract.js";

export const RESOLVER_RESULT = {
  RESOLVED: "resolved",
  NEEDS_MANUAL_INPUT: "needs_manual_input",
};

/** Metrics whose actual can come out of depletion reports. */
const SYSTEM_METRICS = new Set(["cases", "depletions", "new_pods"]);

/**
 * @typedef {Object} ResolverQuery
 * @property {('new_pods'|'cases'|'depletions'|'other'|'custom')} metricType
 * @property {string|null} startDate       ISO date (program window start)
 * @property {string|null} endDate         ISO date (program window end)
 * @property {string|null} distributor     program.distributor_name
 * @property {Array<any>}  targetAccounts  [{id,name,…}] doors the goal measures
 * @property {Array<any>}  targetProducts  [{id,name}] SKUs the goal measures
 * @property {string}      accountId       the tenant
 *
 * @typedef {Object} ResolverResult
 * @property {('resolved'|'needs_manual_input')} status
 * @property {number|null} value    achieved actual when status === 'resolved'
 * @property {('system'|'manual_upload')} source
 * @property {string} reason        why manual input is needed (when applicable)
 * @property {string} [basis]       one line the UI can show as provenance
 * @property {Object} [detail]      { doors, start, end } for the provenance line
 */

/**
 * Can this goal's actual come from system data at all? A capability flag —
 * it does not depend on whether data has been reported yet. This is what
 * incentive_goals.data_source stores.
 */
export function hasAutomaticSource(goal) {
  const metricType = typeof goal === "string" ? goal : goal?.metric_type;
  if (!SYSTEM_METRICS.has(metricType)) return false;
  const products = Array.isArray(goal?.target_products) ? goal.target_products : [];
  // SKU-scoped goals are scored from the distributor's SKU report (see header).
  if (products.length > 0) return false;
  return true;
}

const MANUAL_REASON = {
  custom: "A custom goal is written in free text — enter the result you agreed with the distributor.",
  other: "“Other” has no system source by definition — enter the actual from your own report.",
  sku: "This goal is scoped to specific SKUs. Depletion reports roll up per account, not per SKU, so take the number from the distributor's SKU report.",
  window: "Set the program's start and end dates and this pulls from your depletion reports automatically.",
  distributor: "Set the program's target distributor and this pulls from your depletion reports automatically.",
  no_source: "Depletions & Inventory isn't set up on this workspace, so there's nothing to pull from yet.",
  source_unavailable: "Your depletion reports couldn't be read just now — enter the actual manually or try again.",
  empty: "No depletions have been reported for this distributor and window yet.",
  empty_baseline: "No depletions have been reported for this distributor in the window yet, so there are no new accounts to count.",
};

function manual(reason) {
  return { status: RESOLVER_RESULT.NEEDS_MANUAL_INPUT, value: null, source: "manual_upload", reason };
}

function resolved(value, basis, detail) {
  return { status: RESOLVER_RESULT.RESOLVED, value, source: "system", reason: "", basis, detail };
}

function fmtRange(start, end) {
  const f = (d) => {
    const parsed = new Date(`${String(d).slice(0, 10)}T00:00:00`);
    return Number.isNaN(parsed.getTime())
      ? String(d)
      : parsed.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  };
  return `${f(start)} – ${f(end)}`;
}

/** Restrict rollup rows to the goal's target accounts, when it has any. */
function applyAccountFilter(rows, targetAccounts) {
  const ids = new Set(
    (Array.isArray(targetAccounts) ? targetAccounts : [])
      .map((a) => (typeof a === "string" ? a : a?.id))
      .filter(Boolean)
  );
  if (ids.size === 0) return rows;
  return rows.filter((r) => ids.has(r.outlet_id));
}

/**
 * Resolve the achieved actual for a goal.
 * @param {ResolverQuery} query
 * @returns {Promise<ResolverResult>}
 */
export async function resolveActual(query) {
  const {
    metricType, startDate, endDate, distributor,
    targetAccounts = [], targetProducts = [], accountId,
  } = query || {};

  if (!SYSTEM_METRICS.has(metricType)) {
    return manual(MANUAL_REASON[metricType] || MANUAL_REASON.other);
  }
  if (Array.isArray(targetProducts) && targetProducts.length > 0) return manual(MANUAL_REASON.sku);
  if (!accountId) return manual(MANUAL_REASON.source_unavailable);
  if (!startDate || !endDate) return manual(MANUAL_REASON.window);
  // Every incentive program in this module targets one distributor; without it
  // the sum would be every distributor's volume, which is not the goal.
  if (!distributor) return manual(MANUAL_REASON.distributor);

  const windowRes = await outletRollup({ accountId, start: startDate, end: endDate, distributor });
  if (!windowRes.ok) return manual(MANUAL_REASON[windowRes.reason] || MANUAL_REASON.source_unavailable);

  const rows = applyAccountFilter(windowRes.rows, targetAccounts);
  if (rows.length === 0) {
    return manual(metricType === "new_pods" ? MANUAL_REASON.empty_baseline : MANUAL_REASON.empty);
  }

  const range = fmtRange(startDate, endDate);
  const scope = targetAccounts?.length ? `${rows.length} targeted account${rows.length === 1 ? "" : "s"}` : `${rows.length} account${rows.length === 1 ? "" : "s"}`;

  if (metricType === "cases" || metricType === "depletions") {
    const total = rows.reduce((sum, r) => sum + (Number(r.cases) || 0), 0);
    return resolved(
      Math.round(total * 100) / 100,
      `From your depletion reports · ${scope} · ${range}`,
      { doors: rows.length, start: startDate, end: endDate },
    );
  }

  // new_pods — doors selling in the window that were not selling in the 12
  // months before it. The baseline is deliberately NOT restricted to the goal's
  // target accounts: a door is new against the distributor's whole history.
  const base = baselineWindow(startDate);
  if (!base) return manual(MANUAL_REASON.window);
  const baseRes = await outletRollup({ accountId, start: base.start, end: base.end, distributor });
  if (!baseRes.ok) return manual(MANUAL_REASON[baseRes.reason] || MANUAL_REASON.source_unavailable);

  const before = new Set(baseRes.rows.map((r) => r.outlet_id));
  const newDoors = rows.filter((r) => !before.has(r.outlet_id));
  return resolved(
    newDoors.length,
    `From your depletion reports · accounts new against the 12 months before the window · ${range}`,
    { doors: rows.length, newDoors: newDoors.length, start: startDate, end: endDate },
  );
}
